import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { httpError } from '../../lib/http-error.js';

interface ScopeConfig {
  id: string;
  key: string;
  label: string;
  enabled: boolean;
  sortOrder: number;
}
interface MetricConfig extends ScopeConfig {
  scopeType: string;
}
interface Catalog {
  scopes: ScopeConfig[];
  metrics: MetricConfig[];
}

export function GoalConfigPage() {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const query = useQuery({
    queryKey: ['admin-goal-config'],
    queryFn: async () => {
      const res = await fetch('/v1/admin/goal-config', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: Catalog };
      return body.data;
    },
  });

  useEffect(() => { if (query.data) setCatalog(query.data); }, [query.data]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  async function save(kind: 'scope' | 'metric', row: ScopeConfig | MetricConfig, changes: { label?: string; enabled?: boolean; sortOrder?: number }) {
    const key = `${kind}:${'scopeType' in row ? `${row.scopeType}:` : ''}${row.key}`;
    setBusy((current) => new Set(current).add(key));
    try {
      const path = kind === 'scope'
        ? `/v1/admin/goal-config/scope/${row.key}`
        : `/v1/admin/goal-config/metric/${(row as MetricConfig).scopeType}/${row.key}`;
      const res = await fetch(path, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(changes),
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: ScopeConfig | MetricConfig };
      setCatalog((current) => current ? {
        scopes: kind === 'scope' ? current.scopes.map((item) => item.id === row.id ? body.data : item) : current.scopes,
        metrics: kind === 'metric' ? current.metrics.map((item) => item.id === row.id ? body.data as MetricConfig : item) : current.metrics,
      } : current);
      setToast({ kind: 'ok', text: 'Goals configuration saved' });
    } catch (error) {
      setToast({ kind: 'error', text: error instanceof Error ? error.message : 'Save failed' });
      await query.refetch();
    } finally {
      setBusy((current) => { const next = new Set(current); next.delete(key); return next; });
    }
  }

  if (query.isLoading || !catalog) return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-muted text-sm">Loading Goals configuration…</div>;
  if (query.error) return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-rose-400 text-sm">{(query.error as Error).message}</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Choose which goal scopes and measurements are available. Labels and order apply immediately.
        Disabled options remain on existing goals but cannot be selected for new ones.
      </p>
      {catalog.scopes.map((scope) => {
        const scopeBusy = busy.has(`scope:${scope.key}`);
        const metrics = catalog.metrics.filter((metric) => metric.scopeType === scope.key).sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key));
        return (
          <section key={scope.id} className="bg-bg-panel border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border grid grid-cols-1 md:grid-cols-[1fr_90px_90px] gap-3 items-end bg-bg-card">
              <div>
                <label className="block text-[11px] text-muted mb-1">Scope label · {scope.key}</label>
                <input value={scope.label} disabled={scopeBusy} onChange={(e) => setCatalog((current) => current ? { ...current, scopes: current.scopes.map((item) => item.id === scope.id ? { ...item, label: e.target.value } : item) } : current)} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== query.data?.scopes.find((item) => item.id === scope.id)?.label) void save('scope', scope, { label: e.target.value.trim() }); }} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-[11px] text-muted mb-1">Order</label>
                <input type="number" min="0" value={scope.sortOrder} disabled={scopeBusy} onChange={(e) => setCatalog((current) => current ? { ...current, scopes: current.scopes.map((item) => item.id === scope.id ? { ...item, sortOrder: Number(e.target.value) } : item) } : current)} onBlur={(e) => { const value = Number(e.target.value); if (value !== query.data?.scopes.find((item) => item.id === scope.id)?.sortOrder) void save('scope', scope, { sortOrder: value }); }} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <button type="button" disabled={scopeBusy} onClick={() => void save('scope', scope, { enabled: !scope.enabled })} className={`px-3 py-2 rounded-lg border text-sm ${scope.enabled ? 'bg-accent/20 border-accent text-emerald-300' : 'bg-bg-base border-border text-muted'}`}>{scope.enabled ? 'Enabled' : 'Disabled'}</button>
            </div>
            {metrics.length > 0 && (
              <div className="divide-y divide-border/50">
                {metrics.map((metric) => {
                  const key = `metric:${metric.scopeType}:${metric.key}`;
                  return (
                    <div key={metric.id} className="p-3 grid grid-cols-1 md:grid-cols-[1fr_70px_90px] gap-2 items-center">
                      <div>
                        <input value={metric.label} disabled={busy.has(key)} onChange={(e) => setCatalog((current) => current ? { ...current, metrics: current.metrics.map((item) => item.id === metric.id ? { ...item, label: e.target.value } : item) } : current)} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== query.data?.metrics.find((item) => item.id === metric.id)?.label) void save('metric', metric, { label: e.target.value.trim() }); }} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" />
                        <div className="text-[10px] text-muted mt-1 px-1">{metric.key}</div>
                      </div>
                      <input aria-label={`${metric.label} order`} type="number" min="0" value={metric.sortOrder} disabled={busy.has(key)} onChange={(e) => setCatalog((current) => current ? { ...current, metrics: current.metrics.map((item) => item.id === metric.id ? { ...item, sortOrder: Number(e.target.value) } : item) } : current)} onBlur={(e) => { const value = Number(e.target.value); if (value !== query.data?.metrics.find((item) => item.id === metric.id)?.sortOrder) void save('metric', metric, { sortOrder: value }); }} className="w-full bg-bg-base border border-border rounded-lg px-2 py-2 text-sm text-white" />
                      <button type="button" disabled={busy.has(key)} onClick={() => void save('metric', metric, { enabled: !metric.enabled })} className={`px-3 py-2 rounded-lg border text-sm ${metric.enabled ? 'bg-accent/20 border-accent text-emerald-300' : 'bg-bg-base border-border text-muted'}`}>{metric.enabled ? 'On' : 'Off'}</button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
      {toast && <div className={`fixed bottom-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm shadow-2xl ${toast.kind === 'ok' ? 'bg-bg-panel border-accent text-emerald-300' : 'bg-bg-panel border-rose-800 text-rose-300'}`}>{toast.text}</div>}
    </div>
  );
}
